<template>
  <div class="date-picker" ref="root">
    <button class="date-button date-trigger" @click="toggle">
      <img src="https://cdn.shopify.com/s/files/1/1363/8433/files/calendar.svg?v=1670856557"/>
      {{ displayText }}
      <span class="arrow down"></span>
    </button>

    <!-- Popover calendar -->
    <div v-if="open" class="popover" @keydown.esc="close" role="dialog" aria-label="Date picker">
      <div class="cal-header">
        <button class="nav" @click="prevMonth" aria-label="Previous month">‹</button>
        <div class="label">{{ monthLabel }}</div>
        <button class="nav" @click="nextMonth" aria-label="Next month">›</button>
      </div>

      <div class="cal-grid">
        <div class="dow" v-for="d in dow" :key="d">{{ d }}</div>

        <div
          v-for="cell in cells"
          :key="cell.key"
          class="cell"
          :class="{
            'is-out': cell.isOtherMonth,
            'is-disabled': cell.disabled,
            'is-selected': isSameDay(cell.dateObj, selectedDateObj)
          }"
        >
          <button
            class="daybtn"
            :disabled="cell.disabled"
            @click="pick(cell.dateObj)"
            :aria-label="formatAria(cell.dateObj)"
          >
            {{ cell.day }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LightweightDatePicker",
  props: {
    value: { type: String, default: "" }, // YYYY-MM-DD
    placeholder: { type: String, default: "Select date" },
    id: { type: String, default: () => `date-${Math.random().toString(36).slice(2)}` },
    min: { type: String, default: "" },      // 'YYYY-MM-DD'
    max: { type: String, default: "" },      // 'YYYY-MM-DD'
    minToday: { type: Boolean, default: false },
    disableWeekends: { type: Boolean, default: true }
  },
  data() {
    const today = new Date();
    today.setHours(0,0,0,0);
    return {
      innerValue: this.value,
      open: false,
      viewYear: today.getFullYear(),
      viewMonth: today.getMonth() // 0-11
    };
  },
  watch: {
    value(v) { this.innerValue = v; }
  },
  computed: {
    // Local "today" in YYYY-MM-DD
    todayLocal() {
      const d = new Date();
      d.setHours(0,0,0,0);
      return this.toISO(d);
    },
    resolvedMin() {
      const a = this.min || null;
      const b = this.minToday ? this.todayLocal : null;
      if (a && b) return a > b ? a : b;
      return a || b || null;
    },
    resolvedMax() {
      return this.max || null;
    },
    displayText() {
      if (!this.innerValue) return this.placeholder;
      const dt = this.fromISO(this.innerValue);
      if (!dt) return this.innerValue;
      return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(dt);
    },
    selectedDateObj() {
      return this.fromISO(this.innerValue);
    },
    monthLabel() {
      const d = new Date(this.viewYear, this.viewMonth, 1);
      return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(d);
    },
    dow() {
      // Sun → Sat
      return ["Su","Mo","Tu","We","Th","Fr","Sa"];
    },
    cells() {
      const first = new Date(this.viewYear, this.viewMonth, 1);
      const start = new Date(first);
      start.setDate(first.getDate() - first.getDay()); // Sunday of the grid start

      const out = [];
      for (let i = 0; i < 42; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        d.setHours(0,0,0,0);

        const key = this.toISO(d);
        const isOtherMonth = d.getMonth() !== this.viewMonth;

        // NEW: weekend flag gated by prop
        const isWeekend = (d.getDay() === 0 || d.getDay() === 6);
        let disabled = false;

        if (this.disableWeekends && isWeekend) disabled = true;
        if (!disabled && this.resolvedMin && key < this.resolvedMin) disabled = true;
        if (!disabled && this.resolvedMax && key > this.resolvedMax) disabled = true;

        out.push({
          key,
          dateObj: d,
          day: d.getDate(),
          isOtherMonth,
          disabled,
          isWeekend // (optional if you want to style)
        });
      }
      return out;
    }
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        // pick the base date for the initial view
        const selected = this.selectedDateObj;
        const minObj = this.resolvedMin ? this.fromISO(this.resolvedMin) : null;
        const base = selected || minObj || new Date();

        this.viewYear = base.getFullYear();
        this.viewMonth = base.getMonth();

        this.$nextTick(() =>
          document.addEventListener("click", this.onOutside, { capture: true })
        );
      } else {
        document.removeEventListener("click", this.onOutside, { capture: true });
      }
    },
    close() {
      if (!this.open) return;
      this.open = false;
      document.removeEventListener("click", this.onOutside, { capture: true });
    },
    onOutside(e) {
      const root = this.$refs.root;
      if (root && !root.contains(e.target)) this.close();
    },
    prevMonth() {
      if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; }
      else this.viewMonth--;
    },
    nextMonth() {
      if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; }
      else this.viewMonth++;
    },
    pick(d) {
      if (this.disableWeekends && (d.getDay() === 0 || d.getDay() === 6)) return;

      const iso = this.toISO(d);
      if (this.resolvedMin && iso < this.resolvedMin) return;
      if (this.resolvedMax && iso > this.resolvedMax) return;

      this.innerValue = iso;
      this.$emit("input", this.innerValue);
      this.close();
    },
    toISO(d) {
      // YYYY-MM-DD in local time
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    fromISO(s) {
      if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
      const [y, m, d] = s.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      dt.setHours(0,0,0,0);
      return isNaN(dt) ? null : dt;
    },
    isSameDay(a, b) {
      if (!a || !b) return false;
      return a.getFullYear() === b.getFullYear() &&
             a.getMonth() === b.getMonth() &&
             a.getDate() === b.getDate();
    },
    formatAria(d) {
      return new Intl.DateTimeFormat("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }).format(d);
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onOutside, { capture: true });
  }
};
</script>

<style scoped>
.popover {
  position: absolute;
  margin-top: 8px;
  z-index: 10;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.12);
  padding: 10px;
  width: 280px;
}

.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.cal-header .nav {
  border: none;
  background: transparent;
  font-size: 20px;
  cursor: pointer;
}
.cal-header .label {
  font-weight: 600;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.dow {
  text-align: center;
  font-size: 12px;
  color: #6b7280;
  padding: 4px 0;
}
.cell {
  height: 34px;
}
.daybtn {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: none;
  background: #f9fafb;
  cursor: pointer;
}
.cell.is-out .daybtn { opacity: 0.45; }
.cell.is-disabled .daybtn {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}
.cell.is-selected .daybtn {
  background: #111827;
  color: #fff;
}

/* Existing styles you had */
.date-button{
  margin-bottom: 5%;
  border-radius: 20px;
  border: 1px solid gray;
  padding: 2% 5%;
  color: gray;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.15vw;
  gap: 5px;
}
.date-button img{ width: 10%; }
.arrow{ border: solid black; border-width: 0 2px 2px 0; display:inline-block; padding:2px; }
.arrow.down{ transform: rotate(45deg); -webkit-transform: rotate(45deg); }

@media (max-width: 767px) {
  .date-button{
    font-size: 3vw;
  }
}
</style>
